import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, Group, InputBase, Pill, ScrollArea, useCombobox, type ComboboxItem } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { useParams } from "@tanstack/react-router";

import { useAuth } from "@/api/resources/auth/hooks";
import { useDirectAccessWorkspaces } from "@/api/resources/workspaces/hooks";
import { setCachedWorkspace } from "@/api/resources/workspaces/utils";
import SkeletonBars from "@/components/Skeleton/SkeletonBars";
import { createWorkspacesTree } from "@/components/Workspace/createWorkspacesTree";
import styles from "@/components/Workspace/WorkspaceSelectorGlobal.module.css";
import { SYSTEM_COLORS } from "@/configs/colorScheme";
import { useNavigation } from "@/hooks/useNavigation";
import Icon from "@/utils/icons";
import { setSentryWorkspace } from "@/utils/sentry";

interface WorkspaceSelectorGlobalProps {
  onSelect?: (workspace_id: string) => void;
}
const WorkspaceSelectorGlobal = ({ onSelect }: WorkspaceSelectorGlobalProps) => {
  const { permissions } = useAuth();
  const { goToWorkspace } = useNavigation();
  const { t } = useTranslation();
  const { workspace_id } = useParams({ strict: false });

  const { data: workspaces, isLoading: loadingWorkspaces } = useDirectAccessWorkspaces(permissions);
  const leveledWorkspaces = useMemo(() => createWorkspacesTree(workspaces ?? []), [workspaces]);

  const currentWorkspace = workspaces?.find((workspace) => workspace.id === workspace_id);
  setSentryWorkspace(currentWorkspace);

  type SelectItemData = ComboboxItem & { level: number; abbreviation: string };
  const leveledWorkspacesSelectData = useMemo<SelectItemData[]>(
    () =>
      leveledWorkspaces.map((ws) => ({
        value: ws.id,
        label: ws.name,
        abbreviation: ws.abbreviation,
        level: ws.level,
      })),
    [leveledWorkspaces],
  );

  const handleWorkspaceChange = (workspace_id?: string | null) => {
    if (!workspace_id) return;
    setCachedWorkspace(workspace_id);
    goToWorkspace(workspace_id);
    if (onSelect) onSelect(workspace_id);
  };

  const [search, setSearch] = useState("");
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch("");
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  useHotkeys([
    [
      "mod+K",
      () => {
        if (combobox.dropdownOpened) {
          combobox.closeDropdown();
        } else {
          combobox.openDropdown();
        }
      },
    ],
  ]);

  useEffect(() => {
    // we need to wait for search input before we can select first one
    if (search) {
      combobox.selectFirstOption();
    }
  }, [combobox, search]);

  const options = leveledWorkspacesSelectData
    .filter((item) => item.label.toLowerCase().includes(search.toLowerCase().trim()))
    .map((item) => (
      <Combobox.Option value={item.value} key={item.value} active={item.value === workspace_id}>
        <Group w="100%" justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            {Array(item.level)
              .fill("")
              .map((_, index) => {
                if (index + 1 === item.level) {
                  return <Icon.GO_DOWN_RIGHT key={`${0 + index}`} color={SYSTEM_COLORS.disabled} />;
                }
                return (
                  <span key={`${0 + index}`} className={styles["select-option-spacer"]}>
                    |
                  </span>
                );
              })}
            {item.label}
            <Pill variant="contrast">{item.abbreviation}</Pill>
          </Group>

          {item.value === workspace_id && <Icon.CHECK size={20} />}
        </Group>
      </Combobox.Option>
    ));

  if (loadingWorkspaces) {
    return <SkeletonBars count={1} />;
  }

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(value) => {
        handleWorkspaceChange(value);
        combobox.closeDropdown();
      }}
      // TODO: extract 24rem, possibly use at other selects as well
      width="24rem"
    >
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          rightSection={<Combobox.Chevron />}
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents="none"
          classNames={{
            wrapper: styles["select-wrapper"],
          }}
        >
          {currentWorkspace?.name ?? ""}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => {
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
          placeholder={t("select-workspace")}
        />
        <Combobox.Options>
          <ScrollArea.Autosize type="scroll" mah="16rem">
            {options.length > 0 ? options : <Combobox.Empty>{t("empty.select-results")}</Combobox.Empty>}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default WorkspaceSelectorGlobal;
